/**
 * A project's schedules.
 *
 * The cron field is the part people get wrong, so the form shows the next few firing
 * times as soon as the schedule exists — a wrong expression is far easier to spot as
 * "next run: 3am Sunday" than as five fields.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import type { Schedule } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { useProject } from "@/components/ProjectLayout";
import { Badge, Empty, ErrorState, Field, Loading, Notice, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

const ABSOLUTE = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  day: "numeric",
  month: "short",
});

function Upcoming({ schedule }: { schedule: Schedule }) {
  if (!schedule.enabled) {
    return <span className="faint">Disabled</span>;
  }
  const upcoming = schedule.upcoming ?? [];
  if (upcoming.length === 0) return <span className="faint">—</span>;

  return (
    <div>
      <div>{ABSOLUTE.format(new Date(upcoming[0]!))}</div>
      {upcoming.length > 1 && (
        <div className="faint" style={{ fontSize: 11.5 }}>
          then {upcoming.slice(1).map((iso) => ABSOLUTE.format(new Date(iso))).join(", ")}
        </div>
      )}
    </div>
  );
}

function NewSchedule({ onCreated }: { onCreated: () => void }) {
  const { project } = useProject();
  const [open, setOpen] = useState(false);
  const [scriptName, setScriptName] = useState("");
  const [name, setName] = useState("");
  const [cron, setCron] = useState("0 2 * * *");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const [overlap, setOverlap] = useState("skip");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [fields, setFields] = useState<Record<string, string>>({});

  const { data: scripts } = useAsync(() => endpoints.scripts(project.slug), [project.slug]);

  if (!open) {
    return (
      <button type="button" className="button button--primary" onClick={() => setOpen(true)}>
        New schedule
      </button>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    setFields({});
    try {
      await endpoints.createSchedule(project.slug, scriptName, {
        name,
        cron,
        timezone,
        overlap,
        params: {},
        inputs: {},
        enabled: true,
        notify_on: ["failure"],
      });
      setOpen(false);
      onCreated();
    } catch (caught) {
      if (caught instanceof ApiError && Object.keys(caught.fields).length > 0) {
        setFields(
          Object.fromEntries(
            Object.entries(caught.fields).map(([key, messages]) => [key, messages.join(" ")]),
          ),
        );
        setProblem("Some pinned values were rejected. A schedule has nobody to ask at 2am, so they are checked now.");
      } else {
        setProblem((caught as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card__header">
        <span className="card__title">New schedule</span>
      </div>
      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        <Field label="Script" required htmlFor="sched-script">
          <select
            id="sched-script"
            className="select"
            value={scriptName}
            required
            onChange={(event) => setScriptName(event.target.value)}
          >
            <option value="">Choose a script…</option>
            {(scripts?.items ?? []).map((script) => (
              <option key={script.id} value={script.name}>
                {script.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Name" hint="Lowercase letters, digits, and hyphens." required htmlFor="sched-name">
          <input
            id="sched-name"
            className="input"
            value={name}
            required
            placeholder="nightly"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field
          label="Cron expression"
          hint="Standard five-field cron, as you would write it on a server."
          error={fields.cron}
          required
          htmlFor="sched-cron"
        >
          <input
            id="sched-cron"
            className="input mono"
            value={cron}
            required
            onChange={(event) => setCron(event.target.value)}
          />
        </Field>

        <Field
          label="Timezone"
          hint="2am Sydney is a different instant from 2am UTC."
          htmlFor="sched-tz"
        >
          <input
            id="sched-tz"
            className="input"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
          />
        </Field>

        <Field
          label="If the previous run is still going"
          htmlFor="sched-overlap"
          hint="A nightly job that takes longer than a day is what this decides."
        >
          <select
            id="sched-overlap"
            className="select"
            value={overlap}
            onChange={(event) => setOverlap(event.target.value)}
          >
            <option value="skip">Skip this firing</option>
            <option value="queue">Start anyway</option>
            <option value="cancel_previous">Cancel the previous run</option>
          </select>
        </Field>

        {Object.entries(fields).length > 0 && (
          <Notice tone="warning" title="Pinned values">
            {Object.entries(fields).map(([key, message]) => (
              <div key={key}>
                <strong>{key}</strong>: {message}
              </div>
            ))}
          </Notice>
        )}

        <div className="row">
          <button type="submit" className="button button--primary" disabled={busy}>
            Create schedule
          </button>
          <button type="button" className="button button--ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

export function Schedules() {
  const { project, can } = useProject();
  const [busy, setBusy] = useState<string>();
  const [problem, setProblem] = useState<string>();

  const { data, error, loading, reload } = useAsync(
    () => endpoints.schedules(project.slug),
    [project.slug],
  );

  const act = async (id: string, action: () => Promise<unknown>) => {
    setBusy(id);
    setProblem(undefined);
    try {
      await action();
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(undefined);
    }
  };

  if (loading) return <Loading rows={4} label="Loading schedules" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const schedules = data ?? [];

  return (
    <div className="stack">
      {problem && <Notice tone="danger">{problem}</Notice>}

      {can("pm") && <NewSchedule onCreated={reload} />}

      {schedules.length === 0 ? (
        <div className="card">
          <Empty title="No schedules">
            A schedule runs a script on its own, with its parameters pinned. They can also
            be declared in a repository manifest.
          </Empty>
        </div>
      ) : (
        <div className="card">
          <div className="table__scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Schedule</th>
                  <th>Script</th>
                  <th>When</th>
                  <th>Next</th>
                  <th>Last run</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <tr key={schedule.id}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{schedule.name}</div>
                      {schedule.auto_disabled_reason && (
                        <div className="field__error" style={{ maxWidth: 320 }}>
                          {schedule.auto_disabled_reason}
                        </div>
                      )}
                      {!schedule.enabled && !schedule.auto_disabled_reason && (
                        <Badge>Disabled</Badge>
                      )}
                      {schedule.consecutive_failures > 0 && schedule.enabled && (
                        <Badge tone="warning">
                          {schedule.consecutive_failures} failures in a row
                        </Badge>
                      )}
                    </td>
                    <td>
                      <Link to={`/projects/${project.slug}/scripts/${schedule.script_name}`}>
                        {schedule.script_name}
                      </Link>
                    </td>
                    <td className="mono muted">
                      {schedule.cron ?? schedule.rate}
                      <div className="faint" style={{ fontSize: 11.5 }}>
                        {schedule.timezone}
                      </div>
                    </td>
                    <td className="muted">
                      <Upcoming schedule={schedule} />
                    </td>
                    <td className="muted">
                      <RelativeTime iso={schedule.last_run_at} />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {can("pm") && (
                        <div className="row" style={{ justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="button button--small"
                            disabled={busy === schedule.id}
                            onClick={() =>
                              act(schedule.id, () =>
                                endpoints.updateSchedule(project.slug, schedule.id, {
                                  enabled: !schedule.enabled,
                                }),
                              )
                            }
                          >
                            {schedule.enabled ? "Disable" : "Enable"}
                          </button>
                          <button
                            type="button"
                            className="button button--small button--danger"
                            disabled={busy === schedule.id}
                            onClick={() =>
                              act(schedule.id, () =>
                                endpoints.deleteSchedule(project.slug, schedule.id),
                              )
                            }
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
